import { useState } from "react";

// Mapa de volumen por músculo sobre una silueta anatómica.
//
// Las formas están dibujadas solo para el lado izquierdo y se reflejan con una
// transformación: así el cuerpo sale simétrico sin duplicar cada trazado, y
// corregir un músculo se hace en un único sitio.
//
// El sistema de coordenadas es el del viewBox (100 x 200), no píxeles, de modo
// que la figura escala sin deformarse.

const IDLE = "hsl(var(--secondary))";

// ─── Silueta ──────────────────────────────────────────────────────────────────

const SILHOUETTE_HALF = `
  M50,22 L44,24
  C38,26 32,29 29,35
  C26,41 24,49 22,58
  C20,67 19,76 18,84
  C17,88 18,92 20,93
  L24,92
  C25,85 26,77 28,68
  C29,61 31,53 33,46
  L34,44
  C34,53 34,62 35,70
  C36,78 36,84 37,90
  L36,98
  C35,107 35,116 36,126
  C37,135 38,143 38,150
  C38,160 39,170 40,180
  L40,190 L48,190 L48,180
  C48,170 48,160 48,150
  L48,100 L50,100 Z
`;

// ─── Músculos (mitad izquierda) ───────────────────────────────────────────────

const FRONT_MUSCLES = [
  { group: "Trapecio", d: "M43,24 C46,23 50,23 50,26 L50,31 C46,31 43,28 43,24 Z" },
  { group: "Hombros", d: "M28,36 C31,31 36,29 39,32 C41,36 40,42 37,45 C32,46 28,42 28,36 Z" },
  { group: "Pecho", d: "M39,33 C43,32 48,32 50,35 L50,48 C45,49 40,47 38,43 C36,39 37,35 39,33 Z" },
  { group: "Bíceps", d: "M28,47 C31,45 34,47 34,51 C34,57 33,62 31,65 C28,65 26,62 26,57 C26,52 27,49 28,47 Z" },
  { group: "Antebrazos", d: "M25,67 C28,66 30,68 30,72 C30,78 29,84 27,89 C24,90 22,88 22,83 C22,77 23,71 25,67 Z" },
  { group: "Abdominales", d: "M42,51 C46,50 50,50 50,52 L50,80 C46,80 42,77 42,73 Z" },
  { group: "Cuádriceps", d: "M38,99 C42,98 47,99 48,103 L48,128 C48,135 45,140 41,140 C38,140 36,135 36,127 C36,115 37,105 38,99 Z" },
  { group: "Gemelos", d: "M39,146 C42,145 46,146 46,151 C46,160 45,169 43,175 C40,176 38,173 38,167 C37,159 38,151 39,146 Z" },
];

const BACK_MUSCLES = [
  { group: "Trapecio", d: "M40,25 C44,23 50,23 50,26 L50,42 C45,40 41,33 40,25 Z" },
  { group: "Hombros", d: "M28,36 C31,31 36,29 39,32 C41,36 40,42 37,45 C32,46 28,42 28,36 Z" },
  { group: "Espalda", d: "M37,43 C42,41 50,41 50,44 L50,72 C45,73 41,70 39,64 C37,57 36,49 37,43 Z" },
  { group: "Tríceps", d: "M27,47 C30,45 33,47 33,51 C33,57 32,62 30,65 C27,65 25,62 25,57 C25,52 26,49 27,47 Z" },
  { group: "Antebrazos", d: "M25,67 C28,66 30,68 30,72 C30,78 29,84 27,89 C24,90 22,88 22,83 C22,77 23,71 25,67 Z" },
  { group: "Glúteos", d: "M40,82 C44,80 50,80 50,83 L50,97 C46,99 42,97 40,93 C38,89 38,85 40,82 Z" },
  { group: "Isquiotibiales", d: "M38,101 C42,100 47,101 48,105 L48,128 C48,135 45,140 41,140 C38,140 36,135 36,127 C36,116 37,107 38,101 Z" },
  { group: "Gemelos", d: "M38,145 C42,144 46,145 46,150 C46,160 45,169 43,175 C40,176 38,173 38,167 C37,158 37,150 38,145 Z" },
];

function fillFor(group, intensity) {
  const value = intensity?.[group];
  if (value === undefined || value === null) return IDLE;
  // Suelo de 0.22: un músculo entrenado poco debe seguir distinguiéndose de
  // uno que no se ha tocado en toda la semana.
  const alpha = 0.22 + Math.max(0, Math.min(1, value)) * 0.78;
  return `hsl(var(--primary) / ${alpha.toFixed(2)})`;
}

function Muscles({ muscles, intensity, onHover }) {
  return muscles.map((m, i) => (
    <path
      key={i}
      d={m.d}
      fill={fillFor(m.group, intensity)}
      stroke="hsl(var(--background))"
      strokeWidth={0.5}
      onMouseEnter={() => onHover(m.group)}
      onMouseLeave={() => onHover(null)}
      className="transition-[fill] duration-300 cursor-default"
    >
      <title>{m.group}</title>
    </path>
  ));
}

function Silhouette({ muscles, intensity, label, onHover, compact }) {
  return (
    <div className="flex-1 flex flex-col items-center gap-2 min-w-0">
      <svg
        viewBox="0 0 100 200"
        className={`w-full ${compact ? "max-w-[92px] sm:max-w-[120px]" : "max-w-[150px]"}`}
        role="img"
        aria-label={label}
      >
        <defs>
          <linearGradient id={`body-${label}`} x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="hsl(var(--secondary))" stopOpacity="0.55" />
            <stop offset="100%" stopColor="hsl(var(--secondary))" stopOpacity="0.25" />
          </linearGradient>
        </defs>

        {/* Cabeza y cuello, solo referencia visual */}
        <ellipse cx="50" cy="12" rx="8" ry="9.5" fill={`url(#body-${label})`} />
        <rect x="46" y="19" width="8" height="6" rx="2" fill={`url(#body-${label})`} />

        {/* Cuerpo: mitad izquierda y su reflejo */}
        <g fill={`url(#body-${label})`}>
          <path d={SILHOUETTE_HALF} />
          <g transform="translate(100,0) scale(-1,1)">
            <path d={SILHOUETTE_HALF} />
          </g>
        </g>

        <g>
          <Muscles muscles={muscles} intensity={intensity} onHover={onHover} />
          <g transform="translate(100,0) scale(-1,1)">
            <Muscles muscles={muscles} intensity={intensity} onHover={onHover} />
          </g>
        </g>
      </svg>

      <span className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
        {label}
      </span>
    </div>
  );
}

// compact: en el detalle de una semana cerrada el mapa es contexto, no el
// contenido principal, y a 360 px las siluetas a tamaño completo empujaban el
// desglose de ejercicios fuera de la pantalla.
export function BodyHeatmap({ intensity, counts = {}, compact = false }) {
  const [hovered, setHovered] = useState(null);

  return (
    <div className={compact ? "space-y-2" : "space-y-3"}>
      <div className="flex gap-3 justify-center">
        <Silhouette
          muscles={FRONT_MUSCLES}
          intensity={intensity}
          label="Frente"
          onHover={setHovered}
          compact={compact}
        />
        <Silhouette
          muscles={BACK_MUSCLES}
          intensity={intensity}
          label="Espalda"
          onHover={setHovered}
          compact={compact}
        />
      </div>

      {/* Altura fija: sin ella la tarjeta da un salto al pasar el cursor. */}
      <div className={`${compact ? "hidden sm:flex h-5" : "flex h-5"} items-center justify-center`}>
        {hovered && (
          <p className="text-xs font-semibold text-foreground">
            {hovered}
            <span className="text-muted-foreground font-medium">
              {" · "}
              {counts[hovered] ? `${counts[hovered]} series` : "sin trabajar"}
            </span>
          </p>
        )}
      </div>

      {/* Leyenda de intensidad */}
      <div className="flex items-center justify-center gap-2">
        <span className="text-[9px] text-muted-foreground uppercase tracking-wider">Menos</span>
        <div
          className="h-1.5 w-24 rounded-full"
          style={{
            background:
              "linear-gradient(to right, hsl(var(--secondary)), hsl(var(--primary) / 0.4), hsl(var(--primary)))",
          }}
        />
        <span className="text-[9px] text-muted-foreground uppercase tracking-wider">Más</span>
      </div>
    </div>
  );
}
